import React, { useState, useMemo } from 'react';

const CATEGORY_ORDER = ['Retention', 'Egress', 'GC TF', 'Epigenetic', 'Oncogene', 'S1P metab', 'Post-trans'];

const CATEGORY_COLORS = {
  Retention: '#3498DB',
  Egress: '#2ECC71',
  'GC TF': '#E74C3C',
  Epigenetic: '#9B59B6',
  Oncogene: '#E67E22',
  'S1P metab': '#F39C12',
  'Post-trans': '#1ABC9C',
};

const SENS_BADGE = {
  EZH2_sensitive: { label: 'Sensitive', color: '#27ae60' },
  EZH2_insensitive: { label: 'Insensitive', color: '#e67e22' },
  not_DEG: { label: 'Not DEG', color: '#666' },
  NS_transform: { label: 'NS', color: '#888' },
};

function TpmBar({ value, max }) {
  if (value == null) return <span className="gna-na">-</span>;
  const width = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="gna-tpm-cell">
      <div className="gna-tpm-bar" style={{ width: `${width}%` }} />
      <span>{value.toFixed(1)}</span>
    </div>
  );
}

export default function GnaPathwayTable({ gnaPathway, onSelectGene }) {
  const [sortBy, setSortBy] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  const maxTpm = useMemo(() => {
    if (!gnaPathway) return 1;
    return Math.max(...gnaPathway.flatMap(g => [g.p0 || 0, g.p6 || 0, g.taz || 0]));
  }, [gnaPathway]);

  const grouped = useMemo(() => {
    if (!gnaPathway) return [];

    let genes = [...gnaPathway];
    if (sortBy) {
      genes.sort((a, b) => {
        let va = a[sortBy], vb = b[sortBy];
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va == null) return 1;
        if (vb == null) return -1;
        if (va < vb) return sortAsc ? -1 : 1;
        if (va > vb) return sortAsc ? 1 : -1;
        return 0;
      });
      return [{ category: null, genes }];
    }

    // Group by category
    const groups = [];
    const catMap = {};
    for (const gene of genes) {
      const cat = gene.category || 'Other';
      if (!catMap[cat]) { catMap[cat] = []; groups.push(cat); }
      catMap[cat].push(gene);
    }

    // Sort by predefined order
    groups.sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

    return groups.map(cat => ({ category: cat, genes: catMap[cat] }));
  }, [gnaPathway, sortBy, sortAsc]);

  const handleSort = (key) => {
    if (sortBy === key) {
      if (!sortAsc) { setSortBy(null); setSortAsc(true); }
      else setSortAsc(false);
    } else {
      setSortBy(key);
      setSortAsc(key === 'gene');
    }
  };

  if (!gnaPathway) return <div className="plot-placeholder">No GNA pathway data</div>;

  const sortIcon = (key) => sortBy === key ? (sortAsc ? ' \u25b2' : ' \u25bc') : '';

  return (
    <div className="gna-container">
      <div className="gna-header">
        <h3>GNA13 Retention/Egress Pathway &mdash; Multi-omic Integration</h3>
        <p className="gna-subtitle">
          {gnaPathway.length} genes | Mutation + Expression + H3K27me3 + Mechanism
        </p>
      </div>

      <div className="gna-table-wrapper">
        <table className="gna-table">
          <thead>
            <tr>
              <th className="gna-th-gene" onClick={() => handleSort('gene')}>Gene{sortIcon('gene')}</th>
              <th className="gna-th-desc">Description</th>
              <th className="gna-th-mut">Mutation</th>
              <th className="gna-th-tpm" onClick={() => handleSort('p0')}>P0{sortIcon('p0')}</th>
              <th className="gna-th-tpm" onClick={() => handleSort('p6')}>P6{sortIcon('p6')}</th>
              <th className="gna-th-tpm" onClick={() => handleSort('taz')}>Taz{sortIcon('taz')}</th>
              <th className="gna-th-fc" onClick={() => handleSort('tFC')}>T-FC{sortIcon('tFC')}</th>
              <th className="gna-th-fc" onClick={() => handleSort('eFC')}>E-FC{sortIcon('eFC')}</th>
              <th className="gna-th-sens">Status</th>
              <th className="gna-th-h3k">H3K27me3</th>
              <th className="gna-th-mech">Mechanism</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map(group => (
              <React.Fragment key={group.category || 'all'}>
                {group.category && (
                  <tr className="gna-group-header">
                    <td colSpan={11}>
                      <span
                        className="gna-category-dot"
                        style={{ backgroundColor: CATEGORY_COLORS[group.category] || '#888' }}
                      />
                      {group.category}
                    </td>
                  </tr>
                )}
                {group.genes.map(g => {
                  const badge = SENS_BADGE[g.sens] || SENS_BADGE.not_DEG;
                  const h3k = g.h3k27me3;

                  return (
                    <tr key={g.gene} className="gna-row">
                      <td className="gna-gene">
                        <button
                          className="gna-gene-btn"
                          onClick={() => onSelectGene && onSelectGene(g.gene)}
                        >
                          {g.gene}
                        </button>
                      </td>
                      <td className="gna-desc" title={g.desc}>{g.desc}</td>
                      <td className="gna-mut">
                        {g.mutation ? (
                          <span className="gna-mut-badge">{g.mutation}</span>
                        ) : '\u2014'}
                      </td>
                      <td className="gna-tpm"><TpmBar value={g.p0} max={maxTpm} /></td>
                      <td className="gna-tpm"><TpmBar value={g.p6} max={maxTpm} /></td>
                      <td className="gna-tpm"><TpmBar value={g.taz} max={maxTpm} /></td>
                      <td className={`gna-fc ${(g.tFC || 0) < 0 ? 'neg' : 'pos'}`}>
                        {g.tFC?.toFixed(2)}
                      </td>
                      <td className={`gna-fc ${(g.eFC || 0) < 0 ? 'neg' : 'pos'}`}>
                        {g.eFC?.toFixed(2)}
                      </td>
                      <td>
                        <span className="gna-sens-badge" style={{ backgroundColor: badge.color }}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="gna-h3k">
                        {h3k ? (
                          <span title={`DMSO: ${h3k.dPeaks} peaks, ${h3k.dCov}kb | Taz: ${h3k.tPeaks} peaks, ${h3k.tCov}kb`}>
                            <span className={`gna-h3k-dot ${h3k.responsive ? 'responsive' : ''}`} />
                            {h3k.dPeaks > 0 ? `${h3k.dPeaks}pk` : '\u2014'}
                          </span>
                        ) : '\u2014'}
                      </td>
                      <td className="gna-mech" title={g.mechanism || ''}>
                        {g.mechanism ? g.mechanism.split('|')[0].trim() : '\u2014'}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
